import React, { useEffect, useMemo } from "react";

/**
 * Single-file, preview-safe article page.
 * - No external imports (prevents "File not found" errors in the editor).
 * - Paste your legacy HTML into `legacyHtml` and it will render inside the site frame.
 * - Strips <head>/<style>/<script>/<link> so it doesn't break your app styling.
 * - Optional image rewrite: /images/... -> /images/articles/...
 */

const BRAND = {
  white: "#ffffff",
  charcoal: "#0b0b0f",
  gray: "#6b7280",
  burgundy: "#800020",
  gold: "#b08d57",
  lilac: "#a78bfa", // sponsor-only
};

function Rule({ className = "" }) {
  return <div className={`h-px w-full bg-black/10 ${className}`} />;
}

function Box({ children, className = "" }) {
  return <div className={`border border-black/15 bg-white ${className}`}>{children}</div>;
}

function SafeIcon() {
  // Preview-safe icon (no asset import). Replace with your real logo icon in your app.
  return (
    <svg width="36" height="36" viewBox="0 0 64 64" aria-hidden="true">
      <rect x="6" y="6" width="52" height="52" fill="#0b0b0f" />
      <rect x="12" y="12" width="40" height="40" fill="#ffffff" />
      <path d="M18 42V22h10c7 0 12 4 12 10s-5 10-12 10H18zm6-5h4c4 0 7-2 7-5s-3-5-7-5h-4v10z" fill="#0b0b0f" />
    </svg>
  );
}

function SiteHeader() {
  return (
    <header className="sticky top-0 z-40 bg-white">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="pt-5 sm:pt-6">
          <div className="flex items-center justify-between gap-3">
            <div className="hidden sm:flex items-center gap-4">
              <a
                href="/"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Music × Culture
              </a>
              <a
                href="/"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Art × Fashion
              </a>
            </div>
            <div className="flex items-center gap-4">
              <a
                href="/ams"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                AMS
              </a>
              <a
                href="/login"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Login
              </a>
            </div>
          </div>

          <Rule className="mt-4" />

          <div className="grid grid-cols-1 items-end gap-3 py-4 sm:grid-cols-[1fr_auto_1fr]">
            <div className="hidden sm:block">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">
                Africa Loud Suite
              </div>
              <div className="mt-1 text-xs text-neutral-600">Culture intelligence → creative execution</div>
            </div>

            <a href="/" className="mx-auto flex items-center gap-3 text-center" aria-label="Go to Home">
              <SafeIcon />
              <div>
                <div className="font-serif text-4xl tracking-tight text-neutral-950 sm:text-5xl">Africa Loud</div>
                <div className="-mt-1 text-[11px] font-semibold tracking-[0.25em] uppercase text-neutral-600">
                  Editorial
                </div>
              </div>
            </a>

            <div className="hidden justify-self-end sm:block">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Weekly drops</div>
              <div className="mt-1 text-xs text-neutral-600">Signals · Cities · Strategy</div>
            </div>
          </div>

          <Rule />

          <nav className="flex flex-wrap items-center justify-between gap-2 py-2">
            <div className="flex items-center gap-2">
              <a
                href="/"
                className="px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition bg-white text-black hover:bg-black/5"
              >
                Home
              </a>
              <a
                href="/"
                className="px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition bg-black text-white"
              >
                Culture × Music
              </a>
              <a
                href="/"
                className="px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition bg-white text-black hover:bg-black/5"
              >
                Art × Fashion
              </a>
            </div>

            <div className="hidden items-center gap-2 sm:flex">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Subscribe</div>
              <div className="h-3 w-px bg-black/15" />
              <div className="text-[11px] text-neutral-600">One email · one week</div>
            </div>
          </nav>

          <Rule />
        </div>
      </div>
    </header>
  );
}

function SiteFooter() {
  const links = [
    { label: "Instagram", href: "https://instagram.com/africaloudmag", external: true },
    { label: "Twitter", href: "https://twitter.com/africaloudmag", external: true },
    { label: "TikTok", href: "https://tiktok.com/@africaloudmag", external: true },
    { label: "Terms", href: "/terms" },
    { label: "Privacy", href: "/terms" },
    { label: "Collab", href: "/collab" },
  ];

  return (
    <footer className="mt-12 bg-neutral-800 px-5 py-6">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm font-semibold text-white">Africa Loud</div>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {links.map((l) => (
              <a
                key={l.label}
                href={l.href}
                target={l.external ? "_blank" : undefined}
                rel={l.external ? "noopener noreferrer" : undefined}
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-white/80 hover:text-white"
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
        <div className="mt-4 text-xs text-white/70">© 2026 Africa Loud</div>
      </div>
    </footer>
  );
}

function SponsorStrip() {
  return (
    <div className="mt-6">
      <Box className="p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Partner</div>
            <div className="mt-1 font-serif text-xl tracking-tight text-neutral-950">ON THIRD - Premium Luxury</div>
            <div className="mt-1 text-sm text-neutral-700">Shop the scent with a 10% off offer via the partner link.</div>
          </div>
          <a
            href="https://onthird.co.za/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center border border-black px-5 py-3 text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-950 hover:bg-black hover:text-white"
            style={{ borderColor: BRAND.lilac }}
          >
            Shop the scent · 10% off
            <span className="ml-2">→</span>
          </a>
        </div>
      </Box>
    </div>
  );
}

function stripLegacyHtml(html) {
  // Remove wrappers/head assets/scripts so legacy pages don’t hijack styling.
  let out = html || "";

  // Remove doctype/html/head/body tags entirely
  out = out
    .replace(/<!doctype[^>]*>/gi, "")
    .replace(/<html[^>]*>/gi, "")
    .replace(/<\/html>/gi, "")
    .replace(/<head[\s\S]*?<\/head>/gi, "")
    .replace(/<body[^>]*>/gi, "")
    .replace(/<\/body>/gi, "");

  // Remove scripts/styles/links (common in pasted legacy pages)
  out = out
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<link[^>]*>/gi, "");

  // Kill on* handlers (onclick, onload, etc.) to avoid runtime errors
  out = out.replace(/\son\w+\s*=\s*"[^"]*"/gi, "");
  out = out.replace(/\son\w+\s*=\s*'[^']*'/gi, "");

  return out.trim();
}

function rewriteLegacyImages(html, prefix = "/images/articles/") {
  // /images/foo.png -> /images/articles/foo.png
  return (html || "").replace(/(src=\s*["'])\/?images\//gi, `$1${prefix}`);
}

function normalizeAnchors(html) {
  // If you have legacy links like /echo/articles/x.html, you can remap them here.
  // Current behavior: keep links as-is.
  return html || "";
}

function ArticlePageTemplate({
  seoTitle,
  seoDescription,
  canonicalPath,
  heroTitle,
  heroByline,
  heroImage,
  legacyHtml,
  stripStyles = true,
  rewriteImages = true,
}) {
  const preparedHtml = useMemo(() => {
    let out = legacyHtml || "";
    if (stripStyles) out = stripLegacyHtml(out);
    if (rewriteImages) out = rewriteLegacyImages(out);
    out = normalizeAnchors(out);
    return out;
  }, [legacyHtml, stripStyles, rewriteImages]);

  useEffect(() => {
    // Minimal SEO in preview: update title/description.
    if (typeof document === "undefined") return;
    if (seoTitle) document.title = seoTitle;

    if (seoDescription) {
      let tag = document.querySelector('meta[name="description"]');
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", "description");
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", seoDescription);
    }

    // Canonical (safe in most apps; in this preview it’s fine too)
    if (canonicalPath) {
      let canon = document.querySelector('link[rel="canonical"]');
      if (!canon) {
        canon = document.createElement("link");
        canon.setAttribute("rel", "canonical");
        document.head.appendChild(canon);
      }
      canon.setAttribute("href", canonicalPath);
    }
  }, [seoTitle, seoDescription, canonicalPath]);

  return (
    <div className="min-h-screen bg-white text-neutral-950">
      <SiteHeader />

      <main className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="pt-8">
          <div className="flex items-center gap-3">
            <div className="h-2 w-2" style={{ backgroundColor: BRAND.burgundy }} />
            <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-neutral-600">Culture × Music</div>
          </div>

          {/* Hero */}
          <div className="mt-6 grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
            <Box className="p-6 sm:p-8">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Feature</div>
              <h1 className="mt-4 font-serif text-4xl leading-[1.05] tracking-tight sm:text-6xl">{heroTitle}</h1>
              {heroByline ? <div className="mt-3 text-sm text-neutral-600">{heroByline}</div> : null}
              <div className="mt-6">
                <Rule />
                <div className="mt-3 h-1 w-full" style={{ backgroundColor: BRAND.burgundy }} />
              </div>
            </Box>

            <Box className="relative overflow-hidden">
              <div
                className="h-full min-h-[280px] w-full"
                style={{
                  backgroundImage: `url('${heroImage}')`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
              <div className="absolute bottom-0 left-0 right-0">
                <div className="bg-white/92 p-5 backdrop-blur-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Read</div>
                      <div className="mt-1 text-base font-semibold text-neutral-950">Africa Loud</div>
                    </div>
                    <div className="text-[11px] font-semibold tracking-[0.18em] uppercase" style={{ color: BRAND.burgundy }}>
                      Director's pick
                    </div>
                  </div>
                </div>
              </div>
            </Box>
          </div>

          {/* Sponsor stays on all pages */}
          <SponsorStrip />

          {/* Article body */}
          <div className="mt-8 pb-12">
            <Box className="p-6 sm:p-10">
              <article
                className="max-w-none text-neutral-900 leading-relaxed [&_p]:mb-5 [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:font-serif [&_h2]:text-3xl [&_h2]:tracking-tight [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:font-serif [&_h3]:text-2xl [&_h3]:tracking-tight [&_ul]:my-5 [&_ul]:pl-6 [&_li]:mb-2 [&_a]:underline [&_a]:decoration-black/30 hover:[&_a]:decoration-black"
                dangerouslySetInnerHTML={{ __html: preparedHtml }}
              />
            </Box>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

// --- “Tests” (runs in dev/preview) ---
function runTemplateSanityTests() {
  // eslint-disable-next-line no-console
  console.assert(stripLegacyHtml("<script>alert(1)</script><p>ok</p>").includes("<p>ok</p>"), "stripLegacyHtml should remove script");
  // eslint-disable-next-line no-console
  console.assert(!stripLegacyHtml("<style>h1{}</style><p>ok</p>").includes("<style"), "stripLegacyHtml should remove style");
  // eslint-disable-next-line no-console
  console.assert(
    rewriteLegacyImages('<img src="/images/a.png" />').includes('/images/articles/a.png'),
    "rewriteLegacyImages should rewrite /images/"
  );
}

export default function BeyonceArticle() {
  useEffect(() => {
    if (typeof window !== "undefined") runTemplateSanityTests();
  }, []);

  return (
    <ArticlePageTemplate
      seoTitle="Youth Codes: How African Subcultures Shape the Mainstream:"
      seoDescription="How backyards, townships, campuses and timelines became the laboratories of Africa’s most influential movements."
      canonicalPath="/culture-music/town_talk"
      heroTitle="Youth Codes: How African Subcultures Shape the Mainstream"
      heroByline="Written by Nolwazi M • Africa Loud Culture Desk"
      
      heroImage="/images/articles/#"
      stripStyles={true}
      rewriteImages={true}
      legacyHtml={`

<div class="content">
 <article>
  <h2>What Are Subcultures in the African Context?</h2>
  <p>
    Across Africa, subcultures are more than aesthetic or rebellion—they are survival, creativity, and self-determination. 
    From the Alté movement in Nigeria to Gqom and Amapiano in South Africa, from Kinshasa’s Sapeurs to Nairobi’s shrap (sheng + rap) scene, 
    these spaces offer more than style or music. They offer refuge. They offer vision.
  </p>
  <blockquote>
    “Subcultures in Africa are not borrowed. They’re built from the ground up, coded in slang, stitched in thrifted fabrics, 
    and blasted on bootleg Bluetooth speakers.”
  </blockquote>

  <h2>Who Creates Subcultures?</h2>
  <p>
    Subcultures are not created in boardrooms. They are born in backyards, alleys, WhatsApp groups, and underfunded youth clubs. 
    The creators are young Africans, often economically restricted, politically excluded, but spiritually relentless. 
    Students, hustlers, creatives, misfits, and tech kids all play a role in shaping the textures of identity through fashion, dance, music, and slang.
  </p>
  <p>
    It’s not just about rebellion. It’s about rewriting the rules when the old ones never served you.
  </p>

  <h2>Nigeria’s Alté Scene: Cool Beyond Convention</h2>
  <p>
    Alté is more than an alternative genre. It’s a philosophy. Born in Lagos in the mid-2010s, Alté (short for “alternative”) blends indie visuals, 
    genre-blurring sounds, and a fearless rejection of mainstream conformity. Artists like Santi (Cruel Santino), Odunsi the Engine, 
    and Lady Donli made being "different" cool again. The movement spread across campuses, skateparks, and Tumblr feeds, eventually influencing 
    the global Afrobeats wave without being swallowed by it.
  </p>
  <p>
    The Alté look is a collage of thrifted vintage, grunge, 2000s nostalgia, futuristic hair, and maximalist expression. 
    Piercings, painted nails, mesh tops, anime prints, and cyberpunk accessories are not just style statements, they're visual poetry.
  </p>
  <p>
    Historically, Nigeria's conservative mainstream pop culture left little room for gender-fluid expression or eccentric visuals. 
    The Alté scene emerged as both a protest and a sanctuary, built by outliers who chose vulnerability and digital freedom over conformity. 
    The aesthetic is heavily influenced by diasporic blackness, Tumblr archives, Nollywood camp, and Nigerian street slang, layered to speak 
    the language of a new generation.
  </p>

  <h2>South Africa’s Amapiano Kids: From Kasi to the World</h2>
  <p>
    Born in the hoods ("eKasi" or "eLoxion") of Pretoria and Johannesburg, Amapiano isn’t just a genre, it’s an attitude. Its rise from taxi ranks to TikTok 
    is powered by young DJs, dancers, and producers with no formal backing. Their visual storytelling, fashion (Dickies pants, bucket hats), 
    and linguistic codes (Tsotsitaal, isiZulu, SePedi slang) are part of a larger movement of cultural assertion.
  </p>
  <p>
    The typical Amapiano subculture look merges streetwear with kasi sensibilities: Adidas tracksuits, branded golf shirts, dyed hair, 
    clean sneakers, and oversized sunglasses. Dance is as crucial as the beats, body language speaks as loudly as lyrics.
  </p>
  <blockquote>
    “We are not imitating the West anymore. We are exporting ourselves.” — DJ Stokie
  </blockquote>
  <p>
    This aesthetic and sound emerged as a response to South Africa’s post-apartheid economic stagnation. 
    In a society where access is restricted by class and location, Amapiano gave township youth a global microphone. 
    It represents a new confidence, quiet but magnetic, that says local is not lesser.
  </p>

  <h2>The Internet: Africa’s True Cultural Gatekeeper</h2>
  <p>
    Social media is not a tool, it’s the playground, studio, and stage. Platforms like Instagram, TikTok, and X (Twitter) 
    allow African subcultures to travel without permission. Gen Z creators curate aesthetics on Canva and CapCut, 
    circulate mixtapes via Audiomack, and launch viral dances that become global blueprints. Gatekeeping is dead. Collaboration is king.
  </p>

  <h2>Kinshasa’s Sapeurs: Swagger as Resistance</h2>
  <p>
    In Congo, La Sape-short for the Société des Ambianceurs et des Personnes Élégantes, is a fashion-forward, flamboyant subculture of dandyism. 
    But it’s also deeper: an act of defiance. In a country marked by political instability and economic disparity, 
    Sapeurs dress to impress, not for vanity, but to reclaim dignity. Suits become protest. Color becomes pride.
  </p>
  <p>
    The Sapeur look is high fashion on a DIY budget: tailored suits, silk ties, polished shoes, monocles, and walking sticks, all sourced through networks and hustle.
  </p>
  <p>
    Many Sapeurs live in working-class neighborhoods, but dress like European aristocrats to reframe how they are perceived, by their own society and the world. 
    Sape culture emerged during colonial times as a coded way to mock and master the colonial elite. 
    Today, it's a statement of self-worth, exported through documentaries, music videos, and viral portraits on Instagram.
  </p>

  <h2>Nairobi’s Urban Soundscapes: Gengetone & Shrap</h2>
  <p>
    In Kenya, Nairobi youth are crafting hyperlocal sonic identities. Gengetone, often dismissed as vulgar, and Shrap, 
    the fusion of sheng (urban slang) and rap, are grounded in lived realities, hustle, love, politics, parties. 
    Their audiences are not waiting for radio approval, they stream, share, and remix on their own terms.
  </p>
  <p>
    The look? Athletic streetwear, ripped denim, dyed hair, and DIY tattoos. 
    This visual identity is fueled by skate culture, graffiti, and matatu (minibus) design, bold, unfiltered, and undeniably Kenyan. 
    In a city of stark inequality, these subcultures create micro-worlds where being young and loud is no longer a liability but a power.
  </p>

  <h2>Subcultures as Political Commentary</h2>
  <p>
    Whether it's dance crews reclaiming public space in Kampala or queer fashion collectives in Accra challenging gender norms, 
    African subcultures are not apolitical. They speak where institutions are silent. They carve joy where futures feel stolen. 
    And they often do so with little to no funding, just faith, mobile data, and community.
  </p>
  <p>
    African subcultures are building new languages of pride and resistance. They don’t wait for recognition. They simply create.
  </p>

  <h2>The Global Impact: From Sub to Mainstream</h2>
  <p>
    Today, subcultures like Amapiano, Alté, and Gengetone are no longer “underground.” They are trending sounds, fashion styles, 
    and visual identities exported globally. Western pop, fashion houses, and even K-pop now borrow from these aesthetics. 
    But the question remains, how can Africa retain ownership?
  </p>

  <h3>Paths to Protecting Cultural Credit:</h3>
  <ul>
    <li>Support local infrastructure: grants, venues, incubators</li>
    <li>Digitally archive and tag creators correctly</li>
    <li>Tell the stories on African platforms, by African voices</li>
  </ul>

  <h2>Conclusion: The Culture Will Not Be Gentrified</h2>
  <p>
    The story of African subcultures is one of survival, invention, and joy against the odds. 
    These movements are not side notes to the African narrative, they are the pulse. 
    And as long as African youth have the internet, they’ll shape the future on their own terms.
  </p>
  </article>
  </div>

 `}
    />
  );
}
